// src/components/JSONArrayFilter.js

import React, { useState, useEffect, useRef } from "react";
import {
  FaUpload,
  FaCopy,
  FaFileExport,
  FaArrowUp,
  FaArrowDown,
  FaMoon,
  FaSun,
} from "react-icons/fa";

// For advanced JSON repair logic:
import { jsonrepair } from "jsonrepair";
import JSON5 from "json5";
import { js_beautify as jsBeautify } from "js-beautify";

/**
 * JSONArrayFilter:
 *  - Allows filtering a JSON array using a condition object (like { "age": ">30" }).
 *  - Includes line numbers, file upload, dark mode, fix JSON logic, and so on.
 */
const JSONArrayFilter = () => {
  // ------------------ States ------------------
  const [jsonArrayInput, setJsonArrayInput] = useState("");
  const [filterCondition, setFilterCondition] = useState("");
  const [filteredArray, setFilteredArray] = useState("");
  const [error, setError] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Checkboxes for line numbers
  const [showLineNumbersArray, setShowLineNumbersArray] = useState(true);
  const [showLineNumbersCondition, setShowLineNumbersCondition] = useState(true);

  // Dark Mode
  const [darkMode, setDarkMode] = useState(false);

  // Refs for scrolling & line sync
  const arrayTextRef = useRef(null);
  const arrayLinesRef = useRef(null);
  const conditionTextRef = useRef(null);
  const conditionLinesRef = useRef(null);
  const outputRef = useRef(null);

  // Example data
  const exampleArray = `[
  { "name": "Alice", "age": 25, "city": "New York" },
  { "name": "Bob", "age": 35, "city": "Los Angeles" },
  { "name": "Charlie", "age": 30, "city": "Chicago" },
  { "name": "Diana", "age": 35, "city": "SFDC247" }
]`;
  const exampleCondition = `{
  "age": ">30"
}`;

  // ---------- Dark Mode effect ----------
  useEffect(() => {
    if (darkMode) {
      document.body.style.backgroundColor = "#2d2d2d";
      document.body.style.color = "#f0f0f0";
    } else {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    }
  }, [darkMode]);

  // ---------- line number creation ----------
  const createLineNumbers = (text) => {
    return text.split("\n").map((_, i) => i + 1);
  };

  const [arrayLines, setArrayLines] = useState([]);
  const [conditionLines, setConditionLines] = useState([]);

  useEffect(() => {
    if (showLineNumbersArray) {
      setArrayLines(createLineNumbers(jsonArrayInput));
    }
  }, [jsonArrayInput, showLineNumbersArray]);

  useEffect(() => {
    if (showLineNumbersCondition) {
      setConditionLines(createLineNumbers(filterCondition));
    }
  }, [filterCondition, showLineNumbersCondition]);

  // ---------- Scroll Sync for line # panels ----------
  const syncScrollArray = () => {
    if (arrayTextRef.current && arrayLinesRef.current) {
      arrayLinesRef.current.scrollTop = arrayTextRef.current.scrollTop;
    }
  };
  const syncScrollCondition = () => {
    if (conditionTextRef.current && conditionLinesRef.current) {
      conditionLinesRef.current.scrollTop = conditionTextRef.current.scrollTop;
    }
  };

  // ---------- Utility to parse line & column from error msg ----------
  const extractLineColumn = (message) => {
    const match = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (match) {
      return { line: match[1], col: match[2] };
    }
    return null;
  };

  // ---------- Filter Logic ----------
  const filterArray = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        let arr;
        try {
          arr = JSON.parse(jsonArrayInput);
        } catch (parseErr) {
          const info = extractLineColumn(parseErr.message);
          if (info) {
            setError(
              `Invalid JSON near line ${info.line}, column ${info.col}: ${parseErr.message}`
            );
          } else {
            setError(parseErr.message || "Invalid JSON array input.");
          }
          setFilteredArray("");
          setCopySuccess(false);
          setProcessing(false);
          return;
        }

        let cond;
        try {
          cond = JSON.parse(filterCondition);
        } catch (condErr) {
          const info = extractLineColumn(condErr.message);
          if (info) {
            setError(
              `Invalid Filter Condition near line ${info.line}, column ${info.col}: ${condErr.message}`
            );
          } else {
            setError(condErr.message || "Invalid filter condition input.");
          }
          setFilteredArray("");
          setCopySuccess(false);
          setProcessing(false);
          return;
        }

        if (!Array.isArray(arr)) {
          throw new Error("Input must be a valid JSON array.");
        }

        // Perform filter
        const filtered = arr.filter((item) =>
          Object.keys(cond).every((key) => {
            const conditionValue = cond[key];
            const itemValue = item[key];

            // e.g. { "age": ">30" }
            if (typeof conditionValue === "string") {
              if (conditionValue.startsWith(">")) {
                return itemValue > parseFloat(conditionValue.slice(1));
              } else if (conditionValue.startsWith("<")) {
                return itemValue < parseFloat(conditionValue.slice(1));
              }
            }
            // else strict equality
            return itemValue === conditionValue;
          })
        );

        setFilteredArray(JSON.stringify(filtered, null, 2));
        setError("");
        setCopySuccess(false);
      } catch (err) {
        setFilteredArray("");
        setError(
          err.message || "Invalid JSON or filter condition. Please check your input."
        );
        setCopySuccess(false);
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  // ---------- Clear ----------
  const clearInputs = () => {
    setJsonArrayInput("");
    setFilterCondition("");
    setFilteredArray("");
    setError("");
    setCopySuccess(false);
  };

  // ---------- Use Example ----------
  const useExampleData = () => {
    setJsonArrayInput(exampleArray);
    setFilterCondition(exampleCondition);
    setFilteredArray("");
    setError("");
    setCopySuccess(false);
  };

  // ---------- Copy Output ----------
  const handleCopy = () => {
    if (!filteredArray.trim()) return;
    navigator.clipboard.writeText(filteredArray);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  // ---------- Upload Handlers ----------
  const handleArrayUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonArrayInput(ev.target.result);
      setError("");
      setCopySuccess(false);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  const handleConditionUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProcessing(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setFilterCondition(ev.target.result);
      setError("");
      setCopySuccess(false);
      setProcessing(false);
    };
    reader.readAsText(file);
  };

  // ---------- Scroll Output ----------
  const scrollOutputTop = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = 0;
    }
  };
  const scrollOutputBottom = () => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  };

  // ---------- Fix JSON logic (for the array input) ----------
  const fixJSONForArray = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        // Step 1: Use jsonrepair
        const repaired = jsonrepair(jsonArrayInput);

        // Step 2: Beautify
        const beautified = jsBeautify(repaired, {
          indent_size: 2,
          brace_style: "collapse",
        });

        // Step 3: Additional fixes
        const finalJSON = fixAdditionalJSONIssues(beautified);
        if (!finalJSON) {
          throw new Error("Unable to apply additional fixes.");
        }

        setJsonArrayInput(finalJSON);
        setError("");
      } catch (err) {
        setError(
          "Auto-fix failed for the JSON array input. Please review manually. " + err.message
        );
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  // Similar to what we used in “JSONObjectSorter”
  const fixAdditionalJSONIssues = (input) => {
    try {
      let repairedJSON = input;

      // remove duplicates
      const removeDuplicateKeys = (obj) => {
        if (Array.isArray(obj)) {
          return obj.map(removeDuplicateKeys);
        } else if (typeof obj === "object" && obj !== null) {
          return Object.keys(obj).reduce((acc, key) => {
            acc[key] = removeDuplicateKeys(obj[key]);
            return acc;
          }, {});
        }
        return obj;
      };

      // fix mismatched braces/brackets
      const openBraces = (repairedJSON.match(/{/g) || []).length;
      const closeBraces = (repairedJSON.match(/}/g) || []).length;
      const openBrackets = (repairedJSON.match(/\[/g) || []).length;
      const closeBrackets = (repairedJSON.match(/\]/g) || []).length;

      if (openBraces > closeBraces) {
        repairedJSON += "}".repeat(openBraces - closeBraces);
      } else if (closeBraces > openBraces) {
        repairedJSON = repairedJSON.slice(0, -(closeBraces - openBraces));
      }
      if (openBrackets > closeBrackets) {
        repairedJSON += "]".repeat(openBrackets - closeBrackets);
      } else if (closeBrackets > openBrackets) {
        repairedJSON = repairedJSON.slice(0, -(closeBrackets - openBrackets));
      }

      // fix True/False/Null
      repairedJSON = repairedJSON.replace(/\b(True|False|Null)\b/g, (m) =>
        m.toLowerCase()
      );

      // fix extra quotes around keys
      repairedJSON = repairedJSON.replace(
        /([{,]\s*)["']([a-zA-Z_][a-zA-Z0-9_]*)["']\s*:/g,
        "$1$2:"
      );
      // fix missing colons
      repairedJSON = repairedJSON.replace(/(".*?")\s*(?=["{])/g, "$1: ");
      // convert numeric strings
      repairedJSON = repairedJSON.replace(/"\b(\d+(\.\d+)?)\b"/g, "$1");

      // parse + remove duplicates
      const parsed = JSON5.parse(repairedJSON);
      const cleaned = removeDuplicateKeys(parsed);

      // re-beautify final
      const final = jsBeautify(JSON.stringify(cleaned), {
        indent_size: 2,
        brace_style: "collapse",
      });
      return final;
    } catch (err) {
      return null;
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: "900px" }}>
      {/* Dark Mode Toggle */}
      <div style={{ textAlign: "right", marginBottom: "10px" }}>
        <button
          className="btn btn-sm btn-secondary"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? <FaSun /> : <FaMoon />} {darkMode ? "Light" : "Dark"} Mode
        </button>
      </div>

      {/* Possibly error/spinner */}
      <div className="text-center mb-3">
        {processing && <div className="alert alert-info">Processing...</div>}
        {error && <div className="alert alert-danger">{error}</div>}
        <button className="btn btn-info me-2" onClick={useExampleData}>
          Use Example Data
        </button>
        {/* Fix JSON for the array input */}
        <button
          className="btn btn-warning"
          onClick={fixJSONForArray}
          title="Attempt to fix JSON array input"
        >
          Fix JSON Array
        </button>
        <div style={{ fontSize: "0.85rem", color: "#666", marginTop: "5px" }}>
          <em>
            "Fix JSON" uses <strong>jsonrepair</strong> + additional regex to fix typical
            mistakes. Not guaranteed to solve all problems.
          </em>
        </div>
      </div>

      {/* Text areas for array & condition, each with line numbers + file upload */}
      <div className="row mb-3">
        {/* JSON Array Input */}
        <div className="col-md-6">
          <label style={{ fontWeight: "bold" }}>JSON Array</label>
          <div style={{ position: "relative", display: "flex", height: "300px" }}>
            {showLineNumbersArray && (
              <div
                ref={arrayLinesRef}
                style={{
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                  borderRight: "1px solid #ccc",
                  width: "50px",
                  textAlign: "right",
                  userSelect: "none",
                  color: "#6c757d",
                  paddingTop: "5px",
                }}
              >
                {arrayLines.map((lineNo) => (
                  <div
                    key={lineNo}
                    style={{ height: "1.5em", lineHeight: "1.5em" }}
                  >
                    {lineNo}
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={arrayTextRef}
              className="form-control"
              value={jsonArrayInput}
              onScroll={syncScrollArray}
              onChange={(e) => {
                setJsonArrayInput(e.target.value);
                setError("");
              }}
              style={{
                flex: 1,
                resize: "none",
                borderRadius: 0,
                border: "2px solid #ccc",
                overflowY: "auto",
                fontFamily: "monospace",
                paddingLeft: showLineNumbersArray ? "10px" : "1em",
              }}
              placeholder="Enter JSON array here..."
            />
            {/* Upload icon for array */}
            <label
              htmlFor="arrayUpload"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
              }}
            >
              <FaUpload size={20} />
            </label>
            <input
              id="arrayUpload"
              type="file"
              style={{ display: "none" }}
              accept=".json"
              onChange={handleArrayUpload}
            />
          </div>
        </div>

        {/* Filter Condition Input */}
        <div className="col-md-6">
          <label style={{ fontWeight: "bold" }}>Filter Condition</label>
          <div style={{ position: "relative", display: "flex", height: "300px" }}>
            {showLineNumbersCondition && (
              <div
                ref={conditionLinesRef}
                style={{
                  overflowY: "auto",
                  backgroundColor: "#f8f9fa",
                  borderRight: "1px solid #ccc",
                  width: "50px",
                  textAlign: "right",
                  userSelect: "none",
                  color: "#6c757d",
                  paddingTop: "5px",
                }}
              >
                {conditionLines.map((lineNo) => (
                  <div
                    key={lineNo}
                    style={{ height: "1.5em", lineHeight: "1.5em" }}
                  >
                    {lineNo}
                  </div>
                ))}
              </div>
            )}
            <textarea
              ref={conditionTextRef}
              className="form-control"
              value={filterCondition}
              onScroll={syncScrollCondition}
              onChange={(e) => {
                setFilterCondition(e.target.value);
                setError("");
              }}
              style={{
                flex: 1,
                resize: "none",
                borderRadius: 0,
                border: "2px solid #ccc",
                overflowY: "auto",
                fontFamily: "monospace",
                paddingLeft: showLineNumbersCondition ? "10px" : "1em",
              }}
              placeholder='Enter filter condition (e.g. { "age": ">30" })...'
            />
            {/* Upload icon for condition */}
            <label
              htmlFor="conditionUpload"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                cursor: "pointer",
              }}
            >
              <FaUpload size={20} />
            </label>
            <input
              id="conditionUpload"
              type="file"
              style={{ display: "none" }}
              accept=".json"
              onChange={handleConditionUpload}
            />
          </div>
        </div>
      </div>

      {/* Checkboxes for line # toggles */}
      <div className="mb-3" style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
        <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <input
            type="checkbox"
            checked={showLineNumbersArray}
            onChange={(e) => setShowLineNumbersArray(e.target.checked)}
          />
          <span>Show Line Numbers for JSON Array</span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <input
            type="checkbox"
            checked={showLineNumbersCondition}
            onChange={(e) => setShowLineNumbersCondition(e.target.checked)}
          />
          <span>Show Line Numbers for Filter Condition</span>
        </label>
      </div>

      {/* Filter + Clear Buttons */}
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={filterArray} disabled={processing}>
          {processing ? "Filtering..." : "Filter Array"}
        </button>
        <button className="btn btn-secondary" onClick={clearInputs}>
          Clear
        </button>
      </div>

      {/* Filtered Array Display */}
      {filteredArray && (
        <div className="mt-4">
          <h5 className="text-center">Filtered Array</h5>
          <div
            ref={outputRef}
            style={{
              position: "relative",
              height: "300px",
              overflowY: "auto",
              backgroundColor: "#f8f9fa",
              border: "2px solid #ccc",
              borderRadius: "5px",
            }}
          >
            <pre
              style={{
                margin: 0,
                padding: "10px",
                whiteSpace: "pre",
                fontFamily: "monospace",
              }}
            >
              {filteredArray}
            </pre>
          </div>

          {/* 2 rows of action buttons beneath the output */}
          {/* Row 1: Scroll to Top/Bottom */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button className="btn btn-secondary" onClick={scrollOutputTop}>
              <FaArrowUp /> Scroll to Top
            </button>
            <button className="btn btn-secondary" onClick={scrollOutputBottom}>
              <FaArrowDown /> Scroll to Bottom
            </button>
          </div>

          {/* Row 2: Copy, etc. (Feel free to add Export, Preview, Validate if you want) */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              justifyContent: "center",
              marginTop: "8px",
            }}
          >
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              <FaCopy /> {copySuccess ? "Copied!" : "Copy Output"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONArrayFilter;